import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  mapToIncentivePlan,
  mapToIncentivePlans,
} from 'api/mappers/incentivePlan'
import { useApiClient } from 'contexts/ApiClientContext'
import {
  CreateIncentivePlanRequest,
  DeleteIncentivePlanRequest,
  GetIncentivePlanRequest,
  GetIncentivePlansRequest,
  PatchIncentivePlanRequest,
  PauseIncentivePlanRequest,
  PublishIncentivePlanRequest,
  ResumeIncentivePlanRequest,
} from 'lib/generated/api'

export const QUERY_KEYS = {
  incentivePlan: 'incentivePlan',
  incentivePlans: 'incentivePlans',
} as const

export const incentivePlansQueryKey = ({
  organizationId,
  workspaceId,
  ...queryParams
}: GetIncentivePlansRequest) => [
  QUERY_KEYS.incentivePlans,
  organizationId,
  workspaceId,
  queryParams,
]

export const useGetIncentivePlans = (
  request: GetIncentivePlansRequest,
  defaultTimezone?: string
) => {
  const { incentivePlanApi } = useApiClient()

  return useQuery(
    incentivePlansQueryKey(request),
    () => incentivePlanApi.getIncentivePlans(request),
    {
      enabled:
        !!request.organizationId && !!request.workspaceId && !!defaultTimezone,
      select: data => mapToIncentivePlans(data, defaultTimezone as string),
    }
  )
}

export const useCreateIncentivePlan = () => {
  const queryClient = useQueryClient()
  const { incentivePlanApi } = useApiClient()

  return useMutation(
    (request: CreateIncentivePlanRequest) =>
      incentivePlanApi.createIncentivePlan(request),
    {
      onSuccess: (data, variables) => {
        queryClient.setQueryData(
          incentivePlanQueryKey({
            organizationId: variables.organizationId,
            workspaceId: variables.workspaceId,
            incentivePlanId: data.metadata?.id as string,
          }),
          data
        )
        queryClient.invalidateQueries(
          incentivePlansQueryKey({
            organizationId: variables.organizationId,
            workspaceId: variables.workspaceId,
          })
        )
      },
    }
  )
}

export const incentivePlanQueryKey = ({
  organizationId,
  workspaceId,
  incentivePlanId,
}: GetIncentivePlanRequest) => [
  QUERY_KEYS.incentivePlan,
  organizationId,
  workspaceId,
  incentivePlanId,
]

export const useGetIncentivePlan = (
  request: GetIncentivePlanRequest,
  defaultTimezone?: string
) => {
  const { incentivePlanApi } = useApiClient()

  return useQuery(
    incentivePlanQueryKey(request),
    () => incentivePlanApi.getIncentivePlan(request),
    {
      enabled:
        !!request.organizationId && !!request.workspaceId && !!defaultTimezone,
      select: data => mapToIncentivePlan(data, defaultTimezone as string),
    }
  )
}

export const usePatchIncentivePlan = () => {
  const queryClient = useQueryClient()
  const { incentivePlanApi } = useApiClient()

  return useMutation(
    (request: PatchIncentivePlanRequest) =>
      incentivePlanApi.patchIncentivePlan(request),
    {
      onSuccess: (data, variables) => {
        queryClient.setQueryData(incentivePlanQueryKey(variables), data)
        queryClient.invalidateQueries(
          incentivePlansQueryKey({
            organizationId: variables.organizationId,
            workspaceId: variables.workspaceId,
          })
        )
      },
    }
  )
}

export const usePublishIncentivePlan = () => {
  const queryClient = useQueryClient()
  const { incentivePlanApi } = useApiClient()

  return useMutation(
    (request: PublishIncentivePlanRequest) =>
      incentivePlanApi.publishIncentivePlan(request),
    {
      onSuccess: (data, variables) => {
        queryClient.setQueryData(incentivePlanQueryKey(variables), data)
        queryClient.invalidateQueries(
          incentivePlansQueryKey({
            organizationId: variables.organizationId,
            workspaceId: variables.workspaceId,
          })
        )
      },
    }
  )
}

export const usePauseIncentivePlan = () => {
  const queryClient = useQueryClient()
  const { incentivePlanApi } = useApiClient()

  return useMutation(
    (request: PauseIncentivePlanRequest) =>
      incentivePlanApi.pauseIncentivePlan(request),
    {
      onSuccess: (data, variables) => {
        queryClient.setQueryData(incentivePlanQueryKey(variables), data)
        queryClient.invalidateQueries(
          incentivePlansQueryKey({
            organizationId: variables.organizationId,
            workspaceId: variables.workspaceId,
          })
        )
      },
    }
  )
}

export const useResumeIncentivePlan = () => {
  const queryClient = useQueryClient()
  const { incentivePlanApi } = useApiClient()

  return useMutation(
    (request: ResumeIncentivePlanRequest) =>
      incentivePlanApi.resumeIncentivePlan(request),
    {
      onSuccess: (data, variables) => {
        queryClient.setQueryData(incentivePlanQueryKey(variables), data)
        queryClient.invalidateQueries(
          incentivePlansQueryKey({
            organizationId: variables.organizationId,
            workspaceId: variables.workspaceId,
          })
        )
      },
    }
  )
}

export const useDeleteIncentivePlan = () => {
  const queryClient = useQueryClient()
  const { incentivePlanApi } = useApiClient()

  return useMutation(
    (request: DeleteIncentivePlanRequest) =>
      incentivePlanApi.deleteIncentivePlan(request),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(incentivePlanQueryKey(variables))
        queryClient.invalidateQueries(
          incentivePlansQueryKey({
            organizationId: variables.organizationId,
            workspaceId: variables.workspaceId,
          })
        )
      },
    }
  )
}
